import {COINS_SPENT_NUMBER} from '../../configs/coinsSpentAnimation';
import {MINUS_SIGN} from '../../constants/text';

// Текст рисуется вдвое крупнее и уменьшается вдвое — как подписи отрядов и
// число урона: при приближении камеры он остается четким
export const COINS_SPENT_TEXT_SCALE = 0.5;

// Торможение к концу: быстро в начале, плавно в конце
const easeOutQuad = (share) => 1 - (1 - share) * (1 - share);

const clampShare = (value) => Math.min(1, Math.max(0, Number.isFinite(value) ? value : 0));

/**
 * Сколько монет показать: целое положительное число. Все остальное (ноль у
 * бесплатного найма, мусор в пришедшем по сети экшене) — null: числа нет
 *
 * @param {*} amount
 * @returns {number|null}
 */
export const resolveCoinsSpentAmount = (amount) => {
  const rounded = Math.round(Number(amount));
  return Number.isFinite(rounded) && rounded > 0 ? rounded : null;
};

/**
 * Надпись числа: трата — с типографским минусом, как у числа урона;
 * полученные монеты (кража лепреконов, награда гоблину) — с плюсом
 */
export const formatCoinsSpentText = (amount, {isGain = false} = {}) => `${isGain ? '+' : MINUS_SIGN}${amount}`;

/**
 * Размеры числа и иконки на ячейке такого размера. Шрифт — в пикселях
 * текстуры текста: на поле он вдвое мельче (см. COINS_SPENT_TEXT_SCALE)
 *
 * @param {number} hexSize
 * @returns {{fontPx: number, strokePx: number, iconSize: number, iconScale: number,
 *   iconOutlineScale: number, gap: number, iconShiftY: number}}
 */
export const resolveCoinsSpentSizes = (hexSize) => {
  const {fontSizeFactor, strokeWidthShare, icon} = COINS_SPENT_NUMBER;
  const fontPx = Math.round(hexSize * fontSizeFactor / COINS_SPENT_TEXT_SCALE);
  const iconSize = hexSize * icon.sizeFactor;
  const iconScale = iconSize / icon.textureSize;

  return {
    fontPx,
    strokePx: Math.round(fontPx * strokeWidthShare),
    iconSize,
    iconScale,
    // Текстура обводки считается по уменьшенной копии иконки — ее спрайт во
    // столько же раз крупнее
    iconOutlineScale: iconScale / icon.outlineSourceScale,
    gap: hexSize * icon.gapFactor,
    iconShiftY: hexSize * icon.shiftYFactor,
  };
};

/**
 * Толщина обводки иконки в пикселях текстуры обводки — такая, чтобы на поле
 * она совпала с контуром числа. От размера ячейки не зависит: и число, и
 * иконка растут вместе с ней.
 *
 * Контур текста ложится на край цифр, и наружу выходит его половина: на поле
 * это fontSizeFactor · strokeWidthShare / 2 от размера ячейки. Пиксель
 * текстуры обводки на поле — sizeFactor / (textureSize · outlineSourceScale)
 * от него же
 *
 * @returns {number}
 */
export const resolveCoinsSpentIconOutlineThickness = () => {
  const {fontSizeFactor, strokeWidthShare, icon} = COINS_SPENT_NUMBER;
  const strokeOutsideShare = fontSizeFactor * strokeWidthShare / 2;
  const outlinePixelShare = icon.sizeFactor / (icon.textureSize * icon.outlineSourceScale);

  return Math.max(1, Math.round(strokeOutsideShare / outlinePixelShare));
};

/**
 * Где стоят число и иконка относительно середины пары: число слева, иконка
 * справа, а вся пара — по центру над отрядом. Без иконки (текстура не
 * загрузилась) число стоит по центру одно
 *
 * @param {Object} params
 * @param {number} params.hexSize
 * @param {number} params.textWidth — ширина числа на поле (уже с масштабом)
 * @param {boolean} [params.hasIcon]
 * @returns {{textOffsetX: number, iconOffsetX: number}}
 */
export const resolveCoinsSpentLayout = ({hexSize, textWidth, hasIcon = true}) => {
  if (!hasIcon) {
    return {textOffsetX: 0, iconOffsetX: 0};
  }

  const {iconSize, gap} = resolveCoinsSpentSizes(hexSize);
  const width = Number.isFinite(textWidth) && textWidth > 0 ? textWidth : 0;
  const totalWidth = width + gap + iconSize;

  return {
    textOffsetX: -totalWidth / 2 + width / 2,
    iconOffsetX: totalWidth / 2 - iconSize / 2,
  };
};

/**
 * Число в заданный момент своего пути: на сколько поднялось, во сколько раз
 * увеличено (в начале оно «выпрыгивает») и насколько видно
 *
 * @param {number} travel — пройденная доля пути по времени, 0..1
 * @param {number} hexSize
 * @returns {{rise: number, scale: number, alpha: number}}
 */
export const resolveCoinsSpentPose = (travel, hexSize) => {
  const {riseFactor, durationMs, popMs, popFromScale, fadeFromProgress} = COINS_SPENT_NUMBER;
  const share = clampShare(travel);
  const pop = Math.min(1, share * durationMs / popMs);

  return {
    rise: hexSize * riseFactor * easeOutQuad(share),
    scale: popFromScale + (1 - popFromScale) * easeOutQuad(pop),
    alpha: share <= fadeFromProgress ? 1 : 1 - (share - fadeFromProgress) / (1 - fadeFromProgress),
  };
};

/**
 * Запас высоты числа сверх собственного пути: его набирают прежние числа,
 * когда уступают место новому (см. resolveCoinsSpentStackPush). Набирается не
 * скачком, а за stack.pushMs — от высоты, на которой подъем застал число
 *
 * @param {number} travel — пройденная числом доля пути, 0..1
 * @param {{from: number, to: number, atTravel: number}|null} [push] — последний
 *   подъем: с какого запаса, до какого и на какой доле пути он начался
 * @returns {number}
 */
export const resolveCoinsSpentLift = (travel, push) => {
  if (!push) {
    return 0;
  }

  const {durationMs, stack} = COINS_SPENT_NUMBER;
  const share = clampShare((travel - push.atTravel) * durationMs / stack.pushMs);
  return push.from + (push.to - push.from) * easeOutQuad(share);
};

/**
 * На сколько поднять прежние числа над отрядом, когда под ними всплывает
 * новое, — чтобы оно их не догнало и строки не слились.
 *
 * Новое число идет по тому же пути, только позже, и в начале пути поднимается
 * быстрее, чем прежнее в середине своего: зазор между ними со временем
 * сжимается. Меньше всего он в конце пути прежнего числа — там оно выше нового
 * на riseFactor · travel² (у торможения по квадрату). Не хватает до шага между
 * строками — прежние числа поднимаются на разницу.
 *
 * Считается по последнему из прежних чисел: своего запаса высоты у него нет
 * — поднимают только те числа, под которыми всплыло следующее, а под ним еще
 * ничего не всплывало. Остальные поднимаются на столько же: между собой у них
 * место уже есть
 *
 * @param {Object} params
 * @param {number} params.previousTravel — доля пути, пройденная последним из
 *   прежних чисел, 0..1
 * @param {number} params.hexSize
 * @returns {number} подъем, 0 — места и так хватает
 */
export const resolveCoinsSpentStackPush = ({previousTravel, hexSize}) => {
  const {riseFactor, stack} = COINS_SPENT_NUMBER;
  const share = clampShare(previousTravel);
  const closestGap = hexSize * riseFactor * share * share;

  return Math.max(0, hexSize * stack.lineStepFactor - closestGap);
};
